import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { stripTranslationMarkers } from '@dealstack/frontend/shared/mechanism/i18n/translationMarkers';
import { drawerTransitionProps } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling';
import { Button, Drawer, Flex, rem, TextInput, Title } from '@mantine/core';
import { useEffect, useState } from 'react';
import { Text } from '@dealstack/frontend/shared/components/fundament/Text/Text';
import { useHoldingEventDrawerController } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/HoldingEventDrawer/useHoldingEventDrawerController.ts';
import { HoldingEventRes } from '@dealstack/shared/lib/generated';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import {
	AlertBox,
	AlertSubtitle,
	AlertTitle,
	ConfirmationBox,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/HoldingEventDrawer/styled';
import { DeleteEventCards } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/HoldingEventDrawer/DeleteHoldingEventDrawer/DeleteEventCards.tsx';
import { RegularViewTableItemData } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/RegularViewTable/useRegularViewTableController.ts';

interface Props {
	holding?: RegularViewTableItemData;
	className?: string;
	opened: boolean;
	onClose: () => void;
	refreshSecuritiesList: () => void;
	holdingsEvents: HoldingEventRes[];
}

export const DeleteHoldingEventDrawer = ({
	holding,
	opened,
	onClose,
	className,
	refreshSecuritiesList,
	holdingsEvents = [],
}: Props) => {
	const tCapManage = useTranslationCapManage();
	const [selectedEventIds, setSelectedEventIds] = useState<string[]>([]);
	const [confirmTextValue, setConfirmTextValue] = useState('');
	const [wordInputError, setWordInputError] = useState<string>();
	const confirmDeleteText = tCapManage('holdings.event.confirm');
	const expectedConfirmText = stripTranslationMarkers(confirmDeleteText);

	const { onBulkDelete } = useHoldingEventDrawerController(
		refreshSecuritiesList,
		onClose,
		holding,
		holdingsEvents
	);

	useEffect(() => {
		if (!opened) {
			setSelectedEventIds([]);
			setConfirmTextValue('');
			setWordInputError(undefined);
		}
	}, [opened]);

	const handleToggleSelect = (eventId: string) => {
		if (selectedEventIds.includes(eventId)) {
			setSelectedEventIds(selectedEventIds.filter((id) => id !== eventId));
		} else {
			setSelectedEventIds([...selectedEventIds, eventId]);
		}
	};

	const handleConfirmDelete = async () => {
		if (confirmTextValue !== expectedConfirmText) {
			setWordInputError(tCapManage('holdings.event.match'));
			return;
		}

		if (selectedEventIds.length > 0) {
			await onBulkDelete(selectedEventIds);
			handleClose();
		}
	};

	const handleInputChange = (value: string) => {
		setConfirmTextValue(value);
		setWordInputError(undefined);
	};

	const handleClose = () => {
		setSelectedEventIds([]);
		setConfirmTextValue('');
		setWordInputError(undefined);
		onClose();
	};

	const showConfirmation = selectedEventIds.length > 0;

	return (
		<Drawer
			opened={opened}
			onClose={handleClose}
			position="right"
			size="md"
			className={className}
			transitionProps={drawerTransitionProps}
			trapFocus={false}
			title={
				<Title order={4} fw={600}>
					{tCapManage('holdings.event.delete')}
				</Title>
			}
		>
			<Flex direction="column" justify="space-between" h="100%">
				<Flex direction="column" gap={rem('16px')}>
					<AlertBox>
						<Icon variant="holdings-warning" size="3xl" />
						<Flex direction="column" gap="4px">
							<AlertTitle>{tCapManage('holdings.event.delete_select')}</AlertTitle>
							<AlertSubtitle>
								{tCapManage('holdings.event.delete_lose')}
							</AlertSubtitle>
						</Flex>
					</AlertBox>

					<DeleteEventCards
						holdingsEvents={holdingsEvents}
						selectedEventIds={selectedEventIds}
						onToggleSelect={handleToggleSelect}
						tCapManage={tCapManage}
					/>

					{showConfirmation && (
						<>
							<ConfirmationBox>
								<Text variant="sans/36/200" disableVariantMapping>
									{confirmDeleteText}
								</Text>
							</ConfirmationBox>

							<TextInput
								label={tCapManage('holdings.event.delete_enter')}
								placeholder={tCapManage('holdings.event.delete_enter_placeholder')}
								size="md"
								value={confirmTextValue}
								onChange={(event) => handleInputChange(event.currentTarget.value)}
								error={wordInputError}
							/>
						</>
					)}
				</Flex>

				<Flex direction="column" gap={rem('8px')} mt="auto">
					<Button
						variant="filled"
						color="red"
						size="md"
						onClick={handleConfirmDelete}
						fullWidth
						disabled={
							selectedEventIds.length === 0 ||
							confirmTextValue !== expectedConfirmText
						}
					>
						{selectedEventIds.length > 0
							? `${tCapManage('holdings.event.delete_confirm')} (${selectedEventIds.length})`
							: `${tCapManage('holdings.event.delete_confirm')}`}
					</Button>
					<Button onClick={handleClose} fullWidth variant="default">
						{tCapManage('holdings.event.cancel')}
					</Button>
				</Flex>
			</Flex>
		</Drawer>
	);
};
